import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const SHADES = ["#1074BA", "#F58B23", "#03AB9E", "#0B4F80", "#7FB8E0", "#E0A15B", "#4FC3B8"];

export function Img({ name, alt = "", className = "", eager }) {
  return (
    <img src={`/img/${name}.webp`} srcSet={`/img/${name}-sm.webp 960w, /img/${name}.webp 1920w`} sizes="100vw" alt={alt}
      loading={eager ? "eager" : "lazy"} fetchPriority={eager ? "high" : "auto"} decoding="async" className={className} />
  );
}

export function ServiceHero({ image, eyebrow, title, subtitle, ctas = [], stats = [] }) {
  return (
    <section className="on-photo relative overflow-hidden" data-testid="service-hero">
      <Img name={image} eager className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#090D16] via-[#090D16]/85 to-[#090D16]/20" />
      <div className="relative max-w-[1300px] mx-auto px-4 md:px-8 py-20 md:py-28">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#F58B23] mb-5 fade-up">{eyebrow}</p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight max-w-3xl leading-[1.05] fade-up" data-testid="page-title">{title}</h1>
        <p className="text-slate-200 mt-6 max-w-2xl text-base md:text-lg fade-up">{subtitle}</p>
        <div className="flex flex-wrap gap-3 mt-9 fade-up">
          {ctas.map((c, i) => (
            <a key={c.label} href={c.href} data-testid={`hero-cta-${i}`}
              className={i === 0 ? "px-6 py-3 rounded-md bg-[#F58B23] hover:bg-[#D97618] text-slate-950 font-semibold flex items-center gap-2" : "px-6 py-3 rounded-md border border-slate-400/60 hover:border-white text-white font-semibold backdrop-blur-sm"}>
              {c.label}{i === 0 && <ArrowRight className="h-4 w-4" />}
            </a>
          ))}
        </div>
        {stats.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-14 max-w-4xl">
            {stats.map((s, i) => (
              <div key={s.t} className="rounded-lg border border-white/10 bg-slate-950/50 backdrop-blur-md p-4 fade-up" style={{ animationDelay: `${i * 80}ms`, borderTop: `3px solid ${SHADES[i % SHADES.length]}` }}>
                <p className="font-heading font-bold text-lg">{s.t}</p><p className="text-xs text-slate-400 mt-1">{s.d}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export function Section({ eyebrow, title, intro, children, tint, id }) {
  return (
    <section id={id} className={tint ? "border-y border-slate-800 bg-slate-950/60" : ""}>
      <div className="max-w-[1300px] mx-auto px-4 md:px-8 py-16 md:py-20">
        {eyebrow && <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#03AB9E] mb-2">{eyebrow}</p>}
        {title && <h2 className="text-3xl md:text-4xl font-bold tracking-tight max-w-3xl">{title}</h2>}
        {intro && <p className="text-slate-400 mt-4 max-w-3xl">{intro}</p>}
        <div className={title ? "mt-10" : ""}>{children}</div>
      </div>
    </section>
  );
}

export function ColorCard({ i = 0, icon: Icon, title, desc, items = [], testId }) {
  const c = SHADES[i % SHADES.length];
  return (
    <div data-testid={testId} className="relative rounded-lg p-6 border transition-transform duration-300 hover:-translate-y-1 fade-up overflow-hidden"
      style={{ background: `linear-gradient(160deg, ${c}22, ${c}08 60%)`, borderColor: `${c}55`, animationDelay: `${i * 70}ms` }}>
      <span className="absolute top-0 left-0 h-1 w-full" style={{ background: c }} />
      {Icon && <span className="h-11 w-11 rounded-md grid place-items-center mb-4" style={{ background: `${c}26` }}><Icon className="h-5 w-5" style={{ color: c }} /></span>}
      <h3 className="text-lg font-bold leading-snug">{title}</h3>
      {desc && <p className="text-sm text-slate-300 mt-2">{desc}</p>}
      {items.length > 0 && (
        <ul className="mt-4 space-y-2">
          {items.map((t) => <li key={t} className="flex gap-2 text-sm text-slate-300"><span className="mt-2 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: c }} />{t}</li>)}
        </ul>
      )}
    </div>
  );
}

export function Steps({ steps }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="process-steps">
      {steps.map((s, i) => (
        <div key={s.t} className="relative rounded-lg bg-[#0F172A] border border-slate-800 p-5 fade-up" style={{ animationDelay: `${i * 60}ms` }}>
          <p className="font-mono text-3xl font-bold" style={{ color: SHADES[i % SHADES.length] }}>{String(i + 1).padStart(2, "0")}</p>
          <p className="font-semibold mt-2">{s.t}</p>
          <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{s.d}</p>
        </div>
      ))}
    </div>
  );
}

export function Chips({ items }) {
  return (
    <div className="flex flex-wrap gap-2">
      {items.map((t, i) => <span key={t} className="text-sm px-3 py-1.5 rounded-full border" style={{ borderColor: `${SHADES[i % SHADES.length]}66`, background: `${SHADES[i % SHADES.length]}14` }}>{t}</span>)}
    </div>
  );
}

export function PriceTable({ head, rows, testId }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-800" data-testid={testId}>
      <table className="w-full text-sm">
        <thead><tr className="bg-[#1074BA]/20">{head.map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr></thead>
        <tbody>{rows.map((r) => <tr key={r[0]} className="border-t border-slate-800 odd:bg-slate-900/40">{r.map((c, j) => <td key={j} className={`px-4 py-3 ${j === 0 ? "font-medium" : "text-slate-300"}`}>{c}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

export function Faq({ items }) {
  return (
    <Accordion type="single" collapsible className="max-w-3xl" data-testid="faq">
      {items.map(([q, a], i) => (
        <AccordionItem key={q} value={`q${i}`} className="border-slate-800">
          <AccordionTrigger className="text-left hover:no-underline" data-testid={`faq-q-${i}`}>{q}</AccordionTrigger>
          <AccordionContent className="text-slate-400">{a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function CtaCards({ items }) {
  return (
    <div className="grid md:grid-cols-3 gap-4">
      {items.map((c, i) => (
        <a key={c.t} href="#enquire" data-testid={`cta-card-${i}`} className="group rounded-lg p-6 border transition-colors" style={{ borderColor: `${SHADES[i]}66`, background: `${SHADES[i]}18` }}>
          <p className="font-mono text-xs uppercase tracking-widest" style={{ color: SHADES[i] }}>{c.k}</p>
          <p className="font-bold text-lg mt-2">{c.t}</p>
          <p className="text-sm text-slate-300 mt-2">{c.d}</p>
          <span className="text-xs mt-4 inline-flex items-center gap-1 text-white">Start a discussion <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform" /></span>
        </a>
      ))}
    </div>
  );
}

export const ContactLink = () => <Link to="/contact" className="text-[#03AB9E] underline">contact us</Link>;
